import { m } from "@plannello/i18n/messages";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Form } from "react-aria-components";
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  InlineError,
  Modal,
  Radio,
  RadioGroup,
} from "@/components/ui";
import { spacesQuery } from "@/data/catalog";
import type { Product } from "@/data/types";
import { ApiError, api } from "@/lib/api";
import { toast } from "@/lib/toast";
import type { ContentLanguage } from "./api";
import { nativeName } from "./content";

const LANGUAGES: readonly ContentLanguage[] = ["en", "de"];

/**
 * Product settings dialog for the content language: the language the team writes this product's
 * items and comments in. Product admins only (the menu entry is hidden for everyone else and the
 * server refuses anyway). Explains what the setting changes so nobody flips it by accident.
 */
export function ProductLanguageDialog({
  product,
  name,
  isOpen,
  onOpenChange,
}: {
  product: Product;
  /** Display name of the product. */
  name: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="md">
      <Dialog aria-labelledby="cl-dialog-title">
        {({ close }) => <LanguageForm product={product} name={name} close={close} />}
      </Dialog>
    </Modal>
  );
}

function LanguageForm({ product, name, close }: { product: Product; name: string; close: () => void }) {
  const qc = useQueryClient();
  const current: ContentLanguage = product.contentLanguage ?? "en";
  const [value, setValue] = useState<ContentLanguage>(current);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const save = async () => {
    if (value === current) {
      close();
      return;
    }
    setPending(true);
    setError(undefined);
    try {
      await api<unknown>(`/api/v1/products/${encodeURIComponent(product.key)}/content-language`, {
        method: "PUT",
        body: { contentLanguage: value },
      });
      // Item languages, spellcheck, templates and translation sources all derive from the product.
      await qc.invalidateQueries({ queryKey: spacesQuery.queryKey });
      void qc.invalidateQueries({ queryKey: ["translation"] });
      void qc.invalidateQueries({ queryKey: ["item"] });
      toast.show({ title: m.cl_saved(), tone: "success" });
      close();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? m.cl_forbidden() : m.error_generic());
    } finally {
      setPending(false);
    }
  };

  return (
    <Form
      validationBehavior="aria"
      className="flex min-h-0 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <DialogHeader title={<span id="cl-dialog-title">{m.cl_dialog_title({ name })}</span>} onClose={close} />
      <DialogBody className="flex flex-col gap-4 pb-1">
        <p className="text-pretty">{m.cl_dialog_intro()}</p>
        {error ? <InlineError message={error} /> : null}
        <RadioGroup
          label={m.cl_prop()}
          labelHidden
          value={value}
          onChange={(v) => setValue(v as ContentLanguage)}
          className="max-w-none"
        >
          {LANGUAGES.map((l) => (
            <Radio key={l} value={l} label={nativeName(l)} />
          ))}
        </RadioGroup>
        <div className="flex flex-col gap-1.5 text-xs text-fg-muted">
          <p className="font-medium text-fg">{m.cl_affects_title()}</p>
          <ul className="flex list-disc flex-col gap-1 pl-4 text-pretty">
            <li>{m.cl_affects_spellcheck()}</li>
            <li>{m.cl_affects_templates()}</li>
            <li>{m.cl_affects_translation()}</li>
            <li>{m.cl_affects_items()}</li>
          </ul>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onPress={close}>
          {m.common_cancel()}
        </Button>
        <Button type="submit" variant="primary" isPending={pending}>
          {m.common_save()}
        </Button>
      </DialogFooter>
    </Form>
  );
}
